import { yupResolver } from '@hookform/resolvers/yup';
import { InputAdornment } from '@mui/material';
import { SendCounterProposalInputModel } from 'inputModels';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import ErrorIcon from '@mui/icons-material/Error';
import { IDemandProposalViewModal } from 'viewModels';
import { useTheme } from 'styled-components';
import { Button } from '../Buttons/Button';
import CurrencyInput from '../CurrencyInput';
import { Container, Content } from './styles';
import { ReactComponent as IconCloseCircle } from '../../assets/icons/IconCloseCircle.svg';
import { useApp, useAuth } from '../../context';
import { ReactComponent as IconChat } from '../../assets/icons/icon-chat-circle.svg';
import { ModalChat } from '../Modals/ModalChat';
import { useSolicitation } from '../../context/solicitation';
import { SolicitationController } from '../../controller/SolicitationController';
import { useConversation } from '../../context/conversation';
import { useAlert } from '../../hooks/useAlert';

interface CounterProposalProps {
  isOpenCounterProposal: boolean;
  onRequestClose: () => void;
  demand: IDemandProposalViewModal;
}

export function CounterProposal({
  isOpenCounterProposal,
  demand,
  onRequestClose,
}: CounterProposalProps) {
  const app = useApp();
  const alert = useAlert();
  const theme = useTheme();
  const { user } = useAuth();
  const { setDemandProposals } = useSolicitation();
  const { getConversationIdByParticipantsIds } = useConversation();
  const [conversationIdSelected, setConversationIdSelected] = useState<string>();
  const [isOpenChat, setIsOpenChat] = useState(false);
  const [defaultValues] = useState<SendCounterProposalInputModel>();
  const registerSchema: yup.SchemaOf<SendCounterProposalInputModel> = yup.object({
    value: yup
      .number()
      .required('Informe o valor do serviço')
      .min(1, 'O valor deve ser maior que R$1,00'),
    justification: yup.string(),
    requestsSubmittedUserId: yup.string(),
    solicitationId: yup.string(),
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(registerSchema),
    reValidateMode: 'onChange',
    defaultValues,
  });

  const handleSendCounterProposal = (data: SendCounterProposalInputModel) => {
    SolicitationController.sendCounterProposal({
      justification: data.justification,
      value: data.value,
      requestsSubmittedUserId: user.id,
      solicitationId: demand?.solicitationId || '',
    }).then(() => {
      setDemandProposals((prev) => {
        const newDemandProposals = prev;
        const idx = prev.findIndex((item) => item.solicitationId === demand?.solicitationId);
        if (idx !== -1) {
          newDemandProposals[idx].requestSubmitted.counterProposal = data.value;
          newDemandProposals[idx].requestSubmitted.justificationCounterProposal =
            data.justification;
        }
        return [...newDemandProposals];
      });
    });
  };
  const handleSelectConversation = () => {
    if (user.id && demand.userId) {
      app.setLoading(true);
      const participantsIds = [user.id, demand.userId];
      getConversationIdByParticipantsIds(participantsIds)
        .then((conversationId) => {
          setConversationIdSelected(conversationId);
          setIsOpenChat(true);
        })
        .catch(() => {
          alert.show({ message: 'Não foi possível abrir o chat', type: 'error' });
        })
        .finally(() => {
          app.setLoading(false);
        });
    }
  };

  return (
    <Container>
      {demand?.requestSubmitted?.counterProposal ? (
        <Content className={isOpenCounterProposal ? '' : 'displayNone'}>
          <div className="content">
            <div>
              <h1>Contra proposta</h1>
              <p>R${demand?.requestSubmitted?.counterProposal}</p>
              {errors?.value && (
                <span className="fieldError">
                  <ErrorIcon fontSize="inherit" /> {errors.value.message}
                </span>
              )}
            </div>
            {demand?.requestSubmitted?.justificationCounterProposal && (
              <div>
                <h1>Justificativa</h1>
                <span className="justificationText">
                  {demand?.requestSubmitted?.justificationCounterProposal}
                </span>
              </div>
            )}
            <div className="containerButton">
              <Button
                text="Debater contraproposta"
                dimensions={{ width: '16rem', height: '2rem' }}
                color="tertiary_500"
                textHoverColor="tertiary_500"
                background="transparent"
                style={{ textDecoration: 'underline' }}
                LeftComponent={IconChat}
                styleLeftComponent={{ fill: theme.colors.tertiary_500 }}
                positionLeftElement={5}
                onClick={handleSelectConversation}
              />
            </div>
          </div>
        </Content>
      ) : (
        <Content className={isOpenCounterProposal ? '' : 'displayNone'}>
          <div className="header">
            <button type="submit" onClick={onRequestClose}>
              Fechar <IconCloseCircle />
            </button>
          </div>
          <div className="content">
            <div>
              <h1>Novo valor</h1>
              <Controller
                control={control}
                name="value"
                render={({ field: { onChange, value } }) => (
                  <CurrencyInput
                    value={value}
                    variant="outlined"
                    size="small"
                    className="inputSuggestionValue"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment sx={{ pl: 1 }} position="start">
                          R$
                        </InputAdornment>
                      ),
                    }}
                    onChange={onChange}
                    error={!!errors.value}
                  />
                )}
              />
              {errors?.value && (
                <span className="fieldError">
                  <ErrorIcon fontSize="inherit" /> {errors.value.message}
                </span>
              )}
            </div>
            <div>
              <h1>
                Justificativa <span>(Opcional)</span>
              </h1>
              <Controller
                control={control}
                name="justification"
                render={({ field: { value, onChange, ...rest } }) => (
                  <textarea value={value} onChange={onChange} {...rest} className="justification" />
                )}
              />
            </div>
            <div className="containerButton">
              <Button
                text="Mandar"
                dimensions={{ width: '50%', height: '2rem' }}
                color="background_white"
                backgroundHoverColor="primary"
                background="secondary"
                onClick={handleSubmit(handleSendCounterProposal)}
              />
            </div>
          </div>
        </Content>
      )}
      <ModalChat
        isOpen={isOpenChat}
        onRequestClose={() => setIsOpenChat(false)}
        conversationId={conversationIdSelected}
      />
    </Container>
  );
}
